// Right-pane "library" personality for the dashboard wall in edit
// mode. Mirrors mockup §library-body (cameras-dashboard-v7.html
// L1907–2087): a budget banner at the top, a search input, and a
// list of locations with their member cameras. Clicking a camera
// row places it on the wall in the next available 3×2 slot;
// already-placed cameras render in the "is-on-dash" tone.
//
// Hard cap: 16 tiles per LLD §4. When the wall is at the cap, every
// add affordance disables and the budget banner flips to the danger
// tone.
//
// All wire calls go through the typed clients (camerasClient,
// locationsClient) — the page's `dashboardsClient.update` is owned
// by the wall page itself (Done writes the layout).

import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@/api";
import { camerasClient, type Camera } from "@/api/camerasClient";
import { locationsClient } from "@/api/locationsClient";
import type { DashboardTile } from "@/types/dashboard";
import { DASHBOARD_MAX_TILES } from "@/types/dashboard";

export interface DashboardLibraryPaneProps {
  /** Currently-placed tiles on the wall. Drives is-on-dash state. */
  tiles: DashboardTile[];
  /** Add a single camera (next available slot). No-op when at cap. */
  onAddCamera: (doorbotId: string) => void;
  /** Bulk-add every camera in a location, respecting the cap. */
  onAddLocation: (doorbotIds: string[]) => void;
  /** Drop a tile by id — called from the lib row's "remove from wall" affordance. */
  onRemoveCamera: (doorbotId: string) => void;
}

interface LocationGroup {
  ring_location_id: string;
  display_name: string;
  cameras: Camera[];
}

/** Case-insensitive substring match across location + camera names. */
function matchesQuery(group: LocationGroup, qLower: string): LocationGroup | null {
  if (qLower.length === 0) return group;
  const locHit = group.display_name.toLowerCase().includes(qLower);
  const camHits = group.cameras.filter((c) =>
    (c.description ?? "").toLowerCase().includes(qLower) ||
    c.doorbot_id.toLowerCase().includes(qLower),
  );
  if (locHit) {
    // Whole location matches → keep all cameras.
    return group;
  }
  if (camHits.length === 0) return null;
  return { ...group, cameras: camHits };
}

export function DashboardLibraryPane({
  tiles,
  onAddCamera,
  onAddLocation,
  onRemoveCamera,
}: DashboardLibraryPaneProps): JSX.Element {
  const [cameras, setCameras] = useState<Camera[] | null>(null);
  const [locationNames, setLocationNames] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState<string>("");
  const [openLocs, setOpenLocs] = useState<Set<string>>(() => new Set());
  // The first time data loads, default-open every location group so the
  // operator sees rows without an extra click. Subsequent toggles are
  // user-driven.
  const didDefaultOpen = useRef<boolean>(false);

  // Single best-effort load. Failures render as an inline error rather
  // than blocking the panel; the wall itself doesn't depend on this.
  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const [camList, locList] = await Promise.all([
          camerasClient.listAll({ signal: ctrl.signal }),
          locationsClient.list(ctrl.signal),
        ]);
        if (ctrl.signal.aborted) return;
        setCameras(camList);
        const names = new Map<string, string>();
        for (const loc of locList) {
          names.set(loc.ring_location_id, loc.display_name);
        }
        setLocationNames(names);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `${err.status} ${err.code ?? err.message}`
            : err instanceof Error
              ? err.message
              : "Failed to load camera library";
        setLoadError(msg);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Group cameras by location_id; preserve location order from the
  // names map (insertion order = list order).
  const groups: LocationGroup[] = useMemo(() => {
    if (!cameras) return [];
    const byLoc = new Map<string, Camera[]>();
    for (const cam of cameras) {
      const arr = byLoc.get(cam.location_id) ?? [];
      arr.push(cam);
      byLoc.set(cam.location_id, arr);
    }
    const out: LocationGroup[] = [];
    // Walk the names map first so locations appear in their stable order.
    for (const [id, name] of locationNames) {
      const cams = byLoc.get(id);
      if (cams && cams.length > 0) {
        out.push({ ring_location_id: id, display_name: name, cameras: cams });
        byLoc.delete(id);
      }
    }
    // Any cameras whose location_id wasn't in the locations list (rare
    // but possible if the camera was indexed before the location admin
    // record propagated) get bucketed under "Unassigned".
    if (byLoc.size > 0) {
      const orphans: Camera[] = [];
      for (const arr of byLoc.values()) orphans.push(...arr);
      out.push({
        ring_location_id: "__orphans__",
        display_name: "Unassigned",
        cameras: orphans,
      });
    }
    return out;
  }, [cameras, locationNames]);

  useEffect(() => {
    if (didDefaultOpen.current) return;
    if (groups.length === 0) return;
    const next = new Set<string>();
    for (const g of groups) next.add(g.ring_location_id);
    setOpenLocs(next);
    didDefaultOpen.current = true;
  }, [groups]);

  const visibleGroups: LocationGroup[] = useMemo(() => {
    const qLower = query.trim().toLowerCase();
    const out: LocationGroup[] = [];
    for (const g of groups) {
      const filtered = matchesQuery(g, qLower);
      if (filtered) out.push(filtered);
    }
    return out;
  }, [groups, query]);

  const placedDoorbotIds: Set<string> = useMemo(() => {
    const s = new Set<string>();
    for (const t of tiles) s.add(t.doorbot_id);
    return s;
  }, [tiles]);

  const remaining = DASHBOARD_MAX_TILES - tiles.length;
  const isFull = remaining <= 0;
  const budgetTone: "ok" | "warn" | "full" =
    remaining <= 0 ? "full" : remaining <= 4 ? "warn" : "ok";
  const budgetCls =
    budgetTone === "full"
      ? "v7-lib-budget v7-lib-budget--full"
      : budgetTone === "warn"
        ? "v7-lib-budget v7-lib-budget--warn"
        : "v7-lib-budget";

  const toggleLoc = (id: string): void => {
    setOpenLocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <aside
      className="v7-right-pane v7-right-pane--library is-open"
      data-personality="library"
    >
      <div className="v7-right-pane__body">
        <div className="v7-right-pane__head">
          <div className="v7-right-pane__title-row">
            <h3 className="v7-right-pane__title">Add cameras</h3>
            <span className="v7-right-pane__count">
              {tiles.length}/{DASHBOARD_MAX_TILES}
            </span>
          </div>
          <div className={budgetCls} role="status">
            {isFull ? (
              <>
                Dashboard is <span className="num">full</span> ({DASHBOARD_MAX_TILES} max)
              </>
            ) : (
              <>
                <span className="num">{remaining}</span> of {DASHBOARD_MAX_TILES} cameras left to add
              </>
            )}
          </div>
          <div className="v7-pane-search">
            <span className="ico" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 5 1.49-1.49-5-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search locations or cameras"
              aria-label="Search library"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="v7-right-pane__list v7-library-body">
          {loadError && (
            <div
              className="v7-overview__error"
              role="alert"
              style={{ margin: 8 }}
            >
              {loadError}
            </div>
          )}
          {!loadError && cameras === null && (
            <div className="v7-events-empty">Loading cameras…</div>
          )}
          {!loadError && cameras !== null && visibleGroups.length === 0 && (
            <div className="v7-events-empty">
              {query.length > 0 ? "No matches." : "No cameras in scope."}
            </div>
          )}
          {visibleGroups.map((group) => {
            const isOpen = openLocs.has(group.ring_location_id);
            const placedHere = group.cameras.filter((c) =>
              placedDoorbotIds.has(c.doorbot_id),
            ).length;
            const allPlaced = placedHere === group.cameras.length;
            const addAllDisabled = isFull || allPlaced;
            return (
              <div
                key={group.ring_location_id}
                className={`v7-lib-loc${isOpen ? " is-open" : ""}`}
              >
                <div
                  className="v7-lib-loc__header"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleLoc(group.ring_location_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleLoc(group.ring_location_id);
                    }
                  }}
                >
                  <span className="v7-lib-loc__name">
                    <span className="chev" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <path d="M9 6l6 6-6 6V6z" />
                      </svg>
                    </span>
                    {group.display_name}
                  </span>
                  <span className="v7-lib-loc__count">
                    {placedHere}/{group.cameras.length}
                  </span>
                  <button
                    type="button"
                    className="v7-lib-add-all"
                    title={
                      addAllDisabled
                        ? isFull
                          ? `Dashboard is full (${DASHBOARD_MAX_TILES} max)`
                          : "All cameras already added"
                        : "Add all cameras in this location"
                    }
                    disabled={addAllDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      const ids = group.cameras
                        .filter((c) => !placedDoorbotIds.has(c.doorbot_id))
                        .map((c) => c.doorbot_id);
                      if (ids.length > 0) onAddLocation(ids);
                    }}
                  >
                    + Add all
                  </button>
                </div>
                <div className="v7-lib-loc__cams">
                  {group.cameras.map((cam) => {
                    const placed = placedDoorbotIds.has(cam.doorbot_id);
                    const disabled = !placed && isFull;
                    const rowCls = [
                      "v7-lib-cam",
                      placed ? "is-on-dash" : "",
                      disabled ? "is-disabled" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <div
                        key={cam.doorbot_id}
                        className={rowCls}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-pressed={placed}
                        aria-disabled={disabled}
                        title={
                          disabled
                            ? `Dashboard is full (${DASHBOARD_MAX_TILES} max)`
                            : placed
                              ? "Remove from dashboard"
                              : "Add to dashboard"
                        }
                        onClick={() => {
                          if (disabled) return;
                          if (placed) onRemoveCamera(cam.doorbot_id);
                          else onAddCamera(cam.doorbot_id);
                        }}
                        onKeyDown={(e) => {
                          if (disabled) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (placed) onRemoveCamera(cam.doorbot_id);
                            else onAddCamera(cam.doorbot_id);
                          }
                        }}
                      >
                        <span
                          className={`v7-ck${placed ? " is-checked" : ""}${
                            disabled ? " is-disabled" : ""
                          }`}
                          aria-hidden="true"
                        >
                          {placed && (
                            <svg viewBox="0 0 24 24">
                              <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5z" />
                            </svg>
                          )}
                        </span>
                        <span
                          className="v7-lib-cam__thumb"
                          aria-hidden="true"
                        />
                        <span className="v7-lib-cam__name">
                          {cam.description?.trim() ||
                            `Camera ${cam.doorbot_id.slice(-6)}`}
                        </span>
                        {placed && (
                          <span className="v7-lib-cam__on-tag">On wall</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
